import React, { useEffect, useState } from "react";
import { ExternalLink, Send } from "lucide-react";
import { getSchedulingErrorMessage, saveSchedulingSettings, sendBookingFlow } from "../../../api/schedulingApi";
import { Alert, Field, btnPrimary, btnSecondary, card, inputClass } from "./ui";

export default function SettingsPanel({ botId, settings, onSaved, readOnly = false, canUpdate = true }) {
  const [form, setForm] = useState(settings);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testMessage, setTestMessage] = useState({ kind: "", text: "" });

  useEffect(() => setForm(settings), [settings]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const bookingUrl = `${window.location.origin}/book/${encodeURIComponent(botId)}`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ kind: "", text: "" });
    try {
      const saved = await saveSchedulingSettings(botId, {
        max_days_ahead: Number(form.max_days_ahead),
        min_notice_minutes: Number(form.min_notice_minutes),
        public_booking_enabled: Boolean(form.public_booking_enabled),
        flow_id: (form.flow_id || "").trim() || null,
        flow_type: form.flow_type || "dynamic",
        flow_mode: form.flow_mode,
        flow_cta: form.flow_cta,
        flow_body: form.flow_body,
        flow_patient_selection: Boolean(form.flow_patient_selection),
        flow_allow_new_patients: form.flow_allow_new_patients !== false,
      });
      onSaved(saved);
      setMessage({ kind: "info", text: "Settings saved." });
    } catch (err) {
      setMessage({ kind: "error", text: getSchedulingErrorMessage(err, "Could not save settings.") });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTestMessage({ kind: "", text: "" });
    try {
      await sendBookingFlow(botId, testTo.trim());
      setTestMessage({ kind: "info", text: "Booking form sent. Check WhatsApp." });
    } catch (err) {
      setTestMessage({ kind: "error", text: getSchedulingErrorMessage(err, "Could not send.") });
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <form onSubmit={save} className={`${card} space-y-4`}>
        <h3 className="text-base font-bold text-slate-900">Booking rules</h3>
        <Alert kind={message.kind || "error"}>{message.text}</Alert>
        <p className="text-xs text-slate-500">Business timezone: <b>{settings.timezone}</b>. All working hours use this timezone.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Book up to (days ahead)"><input disabled={readOnly} required type="number" min="1" max="365" className={inputClass} value={form.max_days_ahead} onChange={set("max_days_ahead")} /></Field>
          <Field label="Minimum notice (minutes)" hint="e.g. 60 = no bookings within the next hour"><input disabled={readOnly} required type="number" min="0" className={inputClass} value={form.min_notice_minutes} onChange={set("min_notice_minutes")} /></Field>
        </div>

        <h3 className="pt-2 text-base font-bold text-slate-900">WhatsApp booking form</h3>
        <Field label="WhatsApp Flow ID or name" hint="From WhatsApp Manager → Flows: the numeric Flow ID, or the exact Flow name (e.g. book-appointment).">
          <input disabled={readOnly} className={inputClass} value={form.flow_id || ""} onChange={set("flow_id")} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Flow type" hint="Static: one-screen form, slots filled in when sent. Dynamic: needs the encrypted Flow endpoint.">
            <select disabled={readOnly} className={inputClass} value={form.flow_type || "dynamic"} onChange={set("flow_type")}>
              <option value="static">Static</option>
              <option value="dynamic">Dynamic</option>
            </select>
          </Field>
          <Field label="Mode">
            <select disabled={readOnly} className={inputClass} value={form.flow_mode} onChange={set("flow_mode")}>
              <option value="published">Published</option>
              <option value="draft">Draft (testing)</option>
            </select>
          </Field>
          <Field label="Button text"><input disabled={readOnly} required maxLength={30} className={inputClass} value={form.flow_cta} onChange={set("flow_cta")} /></Field>
        </div>
        <Field label="Message text"><textarea disabled={readOnly} required rows={2} maxLength={1024} className={inputClass} value={form.flow_body} onChange={set("flow_body")} /></Field>
        {(form.flow_type || "dynamic") === "dynamic" && <fieldset className="space-y-2">
          <label className="flex items-start gap-2 text-sm font-semibold text-slate-700">
            <input disabled={readOnly} type="checkbox" className="mt-1" checked={Boolean(form.flow_patient_selection)} onChange={set("flow_patient_selection")} />
            <span>Let the sender choose the patient<span className="block text-xs font-normal text-slate-500">Lists the sender and family members linked to them as parent or guardian. Enable only after publishing the patient-selection Flow JSON in WhatsApp Manager.</span></span>
          </label>
          {form.flow_patient_selection && <label className="ml-6 flex items-center gap-2 text-sm text-slate-700">
            <input disabled={readOnly} type="checkbox" checked={form.flow_allow_new_patients !== false} onChange={set("flow_allow_new_patients")} />
            Allow adding a new patient (child or ward) from WhatsApp
          </label>}
        </fieldset>}
        {canUpdate && <div className="flex justify-end">
          <button type="submit" className={btnPrimary} disabled={saving}>{saving ? "Saving…" : "Save settings"}</button>
        </div>}
      </form>

      <div className={`${card} h-fit space-y-4`}>
        <h3 className="text-base font-bold text-slate-900">Online booking page</h3>
        <p className="text-xs text-slate-500">A public web page where customers can book without logging in. Tick the box, then click Save settings.</p>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input disabled={readOnly} type="checkbox" checked={Boolean(form.public_booking_enabled)} onChange={set("public_booking_enabled")} />
          Allow online booking
        </label>
        {settings.public_booking_enabled ? (
          <div className="flex items-center gap-2">
            <input readOnly className={inputClass} value={bookingUrl} aria-label="Public booking URL" onFocus={(e) => e.target.select()} />
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className={`${btnSecondary} mt-1 shrink-0`}>
              <ExternalLink size={15} /> Open
            </a>
          </div>
        ) : (
          <p className="text-xs text-amber-700">Online booking is off.</p>
        )}
      </div>

      <div className={`${card} h-fit space-y-4`}>
        <h3 className="text-base font-bold text-slate-900">Send booking form</h3>
        <p className="text-xs text-slate-500">Send the WhatsApp booking form to a number, e.g. to test the flow or to help a caller book.</p>
        <Alert kind={testMessage.kind || "error"}>{testMessage.text}</Alert>
        {canUpdate && <><Field label="WhatsApp number (with country code)">
          <input className={inputClass} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="9198xxxxxxxx" />
        </Field>
        <button type="button" className={btnSecondary} onClick={sendTest} disabled={!settings.flow_id || testTo.trim().length < 8}>
          <Send size={15} /> Send form
        </button>
        {!settings.flow_id && <p className="text-xs text-amber-700">Save a Flow ID first.</p>}</>}
      </div>
    </div>
  );
}
